import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pencil, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface PreparedForInlineEditProps {
  quotationId: string;
  value?: string | null;
  fallbackName?: string | null;
  /** react-query keys to invalidate after saving */
  invalidateKeys?: unknown[][];
}

/**
 * Small inline control to change the "Prepared for" name on a quote/programme
 * after it has been generated. Blank falls back to the lead client name.
 */
export const PreparedForInlineEdit = ({
  quotationId,
  value,
  fallbackName,
  invalidateKeys = [],
}: PreparedForInlineEditProps) => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");

  const saveMutation = useMutation({
    mutationFn: async (next: string) => {
      const { error } = await supabase
        .from("quotations")
        .update({ prepared_for: next.trim() || null } as any)
        .eq("id", quotationId);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateKeys.forEach((key) => queryClient.invalidateQueries({ queryKey: key as any }));
      setEditing(false);
      toast.success("Prepared for updated");
    },
    onError: (err: any) => toast.error(err.message || "Failed to update"),
  });

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => {
          setDraft(value || "");
          setEditing(true);
        }}
        className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <Pencil className="h-3 w-3" />
        Prepared for: {value || fallbackName || "not set"}
      </button>
    );
  }

  return (
    <div className="mt-2 flex items-center gap-2">
      <Input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={fallbackName || "Client name"}
        className="h-8 max-w-xs text-sm"
      />
      <Button
        size="sm"
        variant="ghost"
        onClick={() => saveMutation.mutate(draft)}
        disabled={saveMutation.isPending}
      >
        {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={saveMutation.isPending}>
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};
